import { fmtAmt } from '../../lib/format'
import type { Card, Transaction } from '../../types'

function normalizeText(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

function parseSearchAmount(rawQuery: string): { value: number; hasExplicitSign: boolean } | null {
  const normalized = rawQuery
    .trim()
    .replace(/\s+/g, '')
    .replace(/^r\$/i, '')

  if (!normalized) return null

  const hasExplicitSign = normalized.startsWith('+') || normalized.startsWith('-')
  const signal = normalized.startsWith('-') ? -1 : 1
  const unsigned = hasExplicitSign ? normalized.slice(1) : normalized

  if (!/^[\d.,]+$/.test(unsigned) || !/\d/.test(unsigned)) return null

  const lastComma = unsigned.lastIndexOf(',')
  const lastDot = unsigned.lastIndexOf('.')
  const decimalIndex = Math.max(lastComma, lastDot)

  let integerPart = unsigned
  let decimalPart = ''

  if (decimalIndex >= 0) {
    integerPart = unsigned.slice(0, decimalIndex)
    decimalPart = unsigned.slice(decimalIndex + 1)

    if (decimalPart && !/^\d+$/.test(decimalPart)) return null
  }

  const normalizedInteger = integerPart.replace(/[.,]/g, '')
  if (normalizedInteger && !/^\d+$/.test(normalizedInteger)) return null

  const thousandsSeparators = integerPart.match(/[.,]/g) ?? []
  if (thousandsSeparators.length > 0) {
    const groups = integerPart.split(/[.,]/)
    if (
      new Set(thousandsSeparators).size > 1
      || groups.some(group => group.length === 0)
      || groups.slice(1).some(group => group.length !== 3)
    ) {
      return null
    }
  }

  const numeric = Number(`${normalizedInteger || '0'}.${decimalPart || '0'}`)
  if (Number.isNaN(numeric)) return null

  return { value: signal * numeric, hasExplicitSign }
}

function getSignedAmount(transaction: Transaction): number {
  return transaction.kind === 'in' ? transaction.amount : -transaction.amount
}

function matchesAmount(transaction: Transaction, rawQuery: string): boolean {
  const parsedQuery = parseSearchAmount(rawQuery)
  if (!parsedQuery) return false

  const signedAmount = getSignedAmount(transaction)
  const comparableAmount = parsedQuery.hasExplicitSign ? signedAmount : Math.abs(signedAmount)
  const comparableQuery = parsedQuery.hasExplicitSign ? parsedQuery.value : Math.abs(parsedQuery.value)

  if (Math.abs(comparableAmount - comparableQuery) < 0.005) return true

  const formattedAmount = fmtAmt(Math.abs(transaction.amount))
  const normalizedAmount = rawQuery.trim().replace(/\s+/g, '').toLocaleLowerCase()
  const sign = transaction.kind === 'in' ? '+' : '-'

  return [
    formattedAmount,
    `r$${formattedAmount}`,
    `${sign}${formattedAmount}`,
    `${sign}r$${formattedAmount}`,
  ].some(candidate => candidate === normalizedAmount)
}

export function filterTransactionsBySearch(items: Transaction[], rawQuery: string, cards: Card[] = []): Transaction[] {
  const normalizedQuery = normalizeText(rawQuery)
  if (!normalizedQuery) return items

  const cardsById = new Map(cards.map(card => [card.id, card]))

  return items.filter(transaction => (
    normalizeText(transaction.title).includes(normalizedQuery)
    || normalizeText(cardsById.get(transaction.card_id ?? '')?.name ?? '').includes(normalizedQuery)
    || matchesAmount(transaction, rawQuery)
  ))
}
